/**
 * Spreadsheet parsing for the lead import.
 *
 * SheetJS is loaded dynamically, never at app start. It is several hundred KB and
 * only an admin importing a sheet needs it — a sales rep opening the app on a 3G
 * connection should not pay for a feature they will never touch (CLAUDE.md: fast and
 * offline-tolerant beats feature-rich).
 *
 * NOTE ON THE DEPENDENCY: this is installed from https://cdn.sheetjs.com, not from
 * the npm registry. The registry copy (xlsx@0.18.5) is abandoned and carries two
 * unfixed HIGH advisories — prototype pollution and ReDoS — which matter here
 * because this parser ingests files that come from outside the company. SheetJS
 * publishes fixed releases on their own CDN only. See docs/01-architecture.md.
 */

/** Column aliases. Staff export from Arabic and English tools, so both are accepted. */
const HEADER_ALIASES: Record<string, string[]> = {
  full_name: ['full_name', 'name', 'الاسم', 'اسم العميل', 'العميل'],
  phone: ['phone', 'phone_primary', 'mobile', 'الهاتف', 'رقم الهاتف', 'الموبايل', 'التليفون'],
  whatsapp_phone: ['whatsapp', 'whatsapp_phone', 'واتساب', 'رقم الواتساب'],
  email: ['email', 'e-mail', 'البريد', 'البريد الإلكتروني'],
  source: ['source', 'المصدر'],
  client_type: ['client_type', 'type', 'نوع العميل'],
  temperature: ['temperature', 'درجة الاهتمام', 'الاهتمام'],
  budget_min: ['budget_min', 'min_budget', 'الحد الأدنى', 'الميزانية من'],
  budget_max: ['budget_max', 'max_budget', 'الحد الأقصى', 'الميزانية إلى'],
  preferred_areas: ['preferred_areas', 'areas', 'المناطق', 'المناطق المفضلة'],
  bedrooms: ['bedrooms', 'rooms', 'الغرف', 'عدد الغرف'],
};

const normaliseHeader = (h: string) => String(h).trim().toLowerCase().replace(/[‎‏]/g, '');

function mapHeader(header: string): string | null {
  const key = normaliseHeader(header);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.some((a) => normaliseHeader(a) === key)) return field;
  }
  return null;
}

export interface ParsedSheet {
  rows: Record<string, string>[];
  /** Headers in the file that matched nothing — surfaced so a typo is visible. */
  unmappedHeaders: string[];
  matchedFields: string[];
}

export async function parseLeadSheet(file: File): Promise<ParsedSheet> {
  const XLSX = await import('xlsx');

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) return { rows: [], unmappedHeaders: [], matchedFields: [] };

  // raw: false gives formatted strings, which is what we want — a phone number
  // stored as a number in Excel would otherwise arrive as 1.0012e10 and lose its
  // leading zero, and dates would arrive as serial numbers.
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[firstSheet]!, {
    raw: false,
    defval: '',
  });

  const unmapped = new Set<string>();
  const matched = new Set<string>();

  const rows = raw.map((row) => {
    const out: Record<string, string> = {};
    for (const [header, value] of Object.entries(row)) {
      const field = mapHeader(header);
      if (!field) {
        unmapped.add(header);
        continue;
      }
      matched.add(field);
      // sheet_to_json is typed as unknown per cell. With raw:false SheetJS gives
      // formatted strings, but a merged or error cell can still arrive as an
      // object, and String() would turn that into "[object Object]" and import it
      // as if it were a real value.
      out[field] =
        typeof value === 'string'
          ? value.trim()
          : typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : '';
    }
    return out;
  });

  return {
    // Drop rows that are entirely blank — trailing empty rows are extremely common
    // in hand-maintained sheets and would otherwise all report "Name is required".
    rows: rows.filter((r) => Object.values(r).some((v) => v !== '')),
    unmappedHeaders: [...unmapped],
    matchedFields: [...matched],
  };
}

/** A template so people know what the importer expects, rather than guessing. */
export async function downloadTemplate(): Promise<void> {
  const XLSX = await import('xlsx');

  const sheet = XLSX.utils.json_to_sheet([
    {
      full_name: 'مثال: أحمد محمود',
      phone: '01001234567',
      whatsapp_phone: '',
      email: '',
      source: 'meta',
      client_type: 'end_user',
      temperature: 'warm',
      budget_min: '2000000',
      budget_max: '4000000',
      preferred_areas: 'التجمع الخامس,مدينتي',
      bedrooms: '3',
    },
  ]);

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'leads');
  XLSX.writeFile(book, 'leads-template.xlsx');
}
